/**
 * Tough-Bar Builder - Side Cart Module
 * Mobile drawer open/close, scroll lock, and side-cart focus effects
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.sideCart = {
  getDesktopSideCartBreakpoint() {
    return 1855;
  },

  /**
   * Adjusts sidebar height on scroll so it never overlaps the site footer.
   * Called via x-init on #tb-side-cart.
   */
  initSideCartPosition() {
    if (this._sideCartPositionInitDone) {
      this.recalculateSideCartPosition();
      return;
    }

    this._sideCartPositionInitDone = true;

    let ticking = false;
    this._queueSideCartPositionRecalc = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        this.recalculateSideCartPosition();
      });
    };

    window.addEventListener('scroll', this._queueSideCartPositionRecalc, { passive: true });
    window.addEventListener('resize', this._queueSideCartPositionRecalc, { passive: true });

    // Step-3 accessory changes can move the footer without any user scroll.
    this.$watch('selectedAccessories', () => {
      this.$nextTick(() => this.recalculateSideCartPosition());
    });

    this.recalculateSideCartPosition();
  },

  recalculateSideCartPosition() {
    const sideCart = document.getElementById('tb-side-cart');
    if (!sideCart) return;
    if (!this._isDesktopSideCartViewport()) return;

    const header =
      document.querySelector('.header-wrapper') ||
      document.querySelector('header') ||
      document.querySelector('.shopify-section-header');
    const navHeight = header ? header.offsetHeight : 66;
    sideCart.style.top = navHeight + 'px';

    const footer =
      document.querySelector('footer') ||
      document.querySelector('.shopify-section-footer') ||
      document.querySelector('#shopify-section-footer');

    if (footer) {
      const footerTop = footer.getBoundingClientRect().top;
      const maxBottom = Math.min(footerTop, window.innerHeight);
      sideCart.style.height = Math.max(0, maxBottom - navHeight) + 'px';
      return;
    }

    sideCart.style.height = 'calc(100vh - ' + navHeight + 'px)';
  },

  toggleSideCartCollapse() {
    if (this.sideCartCollapsed) {
      this.expandSideCart();
      return;
    }
    this.collapseSideCart();
  },

  collapseSideCart() {
    this.sideCartCollapsed = true;
  },

  expandSideCart() {
    this.sideCartCollapsed = false;
    this.$nextTick(() => this.initSideCartPosition());
  },

  initCartMutationObserver() {
    if (this._cartMutationObserverInitialized) return;
    this._cartMutationObserverInitialized = true;

    this._cartMutationPreviousSnapshot = this._buildCartMutationSnapshot(this.cartItems);

    this.$watch('cartItems', nextCartItems => {
      const previousSnapshot = this._cartMutationPreviousSnapshot || new Map();
      const nextSnapshot = this._buildCartMutationSnapshot(nextCartItems);
      const { hasLineItemChange, affectedTokens } = this._getAffectedSideCartTokens(previousSnapshot, nextSnapshot);

      this._cartMutationPreviousSnapshot = nextSnapshot;

      if (!hasLineItemChange) return;
      this._queueCartMutationFocus(affectedTokens);
    });
  },

  _buildCartMutationSnapshot(cartItems = []) {
    const snapshot = new Map();
    const sourceItems = Array.isArray(cartItems) ? cartItems : [];

    sourceItems.forEach(item => {
      const token = this.getSideCartLineToken(item);
      if (!token) return;

      const existing = snapshot.get(token);
      if (existing) {
        existing.count += 1;
        return;
      }

      snapshot.set(token, { token, count: 1 });
    });

    return snapshot;
  },

  _getAffectedSideCartTokens(previousSnapshot, nextSnapshot) {
    const previous = previousSnapshot instanceof Map ? previousSnapshot : new Map();
    const next = nextSnapshot instanceof Map ? nextSnapshot : new Map();
    const affectedTokens = [];
    let hasLineItemChange = false;

    next.forEach((entry, token) => {
      const previousEntry = previous.get(token);
      if (!previousEntry) {
        hasLineItemChange = true;
        affectedTokens.push(token);
        return;
      }

      if (previousEntry.count !== entry.count) {
        hasLineItemChange = true;
        affectedTokens.push(token);
      }
    });

    previous.forEach((_entry, token) => {
      if (!next.has(token)) {
        hasLineItemChange = true;
      }
    });

    return {
      hasLineItemChange,
      affectedTokens: Array.from(new Set(affectedTokens))
    };
  },

  _queueCartMutationFocus(affectedTokens = []) {
    const tokens = Array.isArray(affectedTokens)
      ? affectedTokens
          .map(token => this._normalizeSideCartToken(token))
          .filter(Boolean)
      : [];

    this._pendingCartMutationHasChange = true;
    const pendingTokens = Array.isArray(this._pendingCartMutationTokens)
      ? this._pendingCartMutationTokens
      : [];
    const pendingTokenSet = new Set(
      pendingTokens
        .map(token => this._normalizeSideCartToken(token))
        .filter(Boolean)
    );
    tokens.forEach(token => pendingTokenSet.add(token));
    this._pendingCartMutationTokens = Array.from(pendingTokenSet);

    if (this._cartMutationFlushTimer) {
      window.clearTimeout(this._cartMutationFlushTimer);
    }

    this._cartMutationFlushTimer = window.setTimeout(() => {
      this._cartMutationFlushTimer = undefined;
      this._flushCartMutationFocus();
    }, 220);
  },

  _flushCartMutationFocus() {
    const hasChange = Boolean(this._pendingCartMutationHasChange);
    const affectedTokens = Array.isArray(this._pendingCartMutationTokens)
      ? [...this._pendingCartMutationTokens]
      : [];

    this._pendingCartMutationHasChange = false;
    this._pendingCartMutationTokens = [];

    if (!hasChange) return;
    this._focusSideCartByTokens(affectedTokens, {
      align: 'top',
      autoOpen: false
    });
  },

  getSideCartLineToken(item) {
    if (!item || typeof item !== 'object') return '';

    const section = this._normalizeSideCartToken(item.section);
    const handle = this._normalizeSideCartToken(item.handle || item.product?.handle);
    if (!handle) return '';

    const normalizedAccessoryId = typeof this.normalizeAccessoryId === 'function'
      ? this.normalizeAccessoryId(item.accessoryId)
      : item.accessoryId;
    const accessoryId = this._normalizeSideCartToken(normalizedAccessoryId);
    const deviceType = this._normalizeSideCartToken(item.deviceType);
    const sourceKitId = this._normalizeSideCartToken(item.sourceKitId);

    return [section || 'item', handle, accessoryId || '-', deviceType || '-', sourceKitId || '-'].join('|');
  },

  _focusSideCartByTokens(tokens = [], options = {}) {
    const normalizedTokens = Array.isArray(tokens)
      ? tokens
          .map(token => this._normalizeSideCartToken(token))
          .filter(Boolean)
      : [];

    const isDesktop = this._isDesktopSideCartViewport();
    const autoOpen = options?.autoOpen !== false;
    const wasCollapsed = Boolean(this.sideCartCollapsed);
    const wasDrawerClosed = !this.sideCartDrawerOpen;
    const align = options?.align === 'top' ? 'top' : 'center';

    if (autoOpen) {
      this._openSideCartForFocus(isDesktop);
    }

    if (normalizedTokens.length === 0) {
      this._clearSideCartFocusEffects();
      return;
    }

    const isClosed = isDesktop ? Boolean(this.sideCartCollapsed) : !this.sideCartDrawerOpen;
    if (isClosed) {
      this._clearSideCartFocusEffects();
      return;
    }

    const focusDelay = isDesktop
      ? ((autoOpen && wasCollapsed) ? 240 : 70)
      : ((autoOpen && wasDrawerClosed) ? 360 : 120);

    if (this._sideCartFocusDelayTimer) {
      window.clearTimeout(this._sideCartFocusDelayTimer);
      this._sideCartFocusDelayTimer = undefined;
    }

    this.$nextTick(() => {
      this._sideCartFocusDelayTimer = window.setTimeout(() => {
        const container = this._getActiveSideCartItemsContainer();
        if (!container) return;

        const matchingCards = this._getMatchingSideCartCards(container, normalizedTokens);
        if (matchingCards.length === 0) {
          this._clearSideCartFocusEffects();
          return;
        }

        this._scrollSideCartContainerToCard(container, matchingCards[0], align);
        this._applySideCartFocusEffects(matchingCards);
      }, focusDelay);
    });
  },

  _openSideCartForFocus(isDesktop = this._isDesktopSideCartViewport()) {
    if (isDesktop) {
      if (this.sideCartCollapsed) this.expandSideCart();
      return;
    }

    if (!this.sideCartDrawerOpen) {
      this.openSideCartDrawer();
    }
  },

  _isDesktopSideCartViewport() {
    return window.innerWidth >= this.getDesktopSideCartBreakpoint();
  },

  _normalizeSideCartToken(value) {
    if (typeof this.normalizeSummaryProductToken === 'function') {
      return this.normalizeSummaryProductToken(value);
    }

    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
  },

  _getActiveSideCartItemsContainer() {
    const desktopContainer = document.getElementById('tb-side-cart-items');
    const mobileContainer = document.getElementById('tb-mobile-drawer-items');

    return this._isDesktopSideCartViewport() ? desktopContainer : mobileContainer;
  },

  _getMatchingSideCartCards(container, handleTokens = []) {
    if (!container || !Array.isArray(handleTokens) || handleTokens.length === 0) return [];

    const tokenSet = new Set(
      handleTokens
        .map(token => this._normalizeSideCartToken(token))
        .filter(Boolean)
    );

    return Array.from(container.querySelectorAll('.tb-side-cart-item-card'))
      .filter(card => tokenSet.has(this._normalizeSideCartToken(card?.dataset?.cartItemToken)));
  },

  _scrollSideCartContainerToCard(container, card, align = 'center') {
    if (!container || !card) return;

    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    if (containerRect.height > 0) {
      const offsetTop = cardRect.top - containerRect.top;
      const topOffset = align === 'top' ? 12 : ((containerRect.height - cardRect.height) / 2);
      const targetTop = container.scrollTop + offsetTop - topOffset;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      return;
    }

    if (typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  },

  _applySideCartFocusEffects(cards = []) {
    if (!Array.isArray(cards) || cards.length === 0) return;

    this._clearSideCartFocusEffects();

    const uniqueCards = Array.from(new Set(cards));
    uniqueCards.forEach(card => {
      card.classList.remove('tb-side-cart-item--storm', 'tb-side-cart-item--jiggle');
      // Restart CSS animations for repeated clicks on the same card.
      void card.offsetWidth;
      card.classList.add('tb-side-cart-item--storm', 'tb-side-cart-item--jiggle');
    });

    this._sideCartFocusedCards = uniqueCards;

    this._sideCartJiggleTimer = window.setTimeout(() => {
      this._sideCartFocusedCards.forEach(card => card.classList.remove('tb-side-cart-item--jiggle'));
      this._sideCartJiggleTimer = undefined;
    }, 1400);

    this._sideCartStormTimer = window.setTimeout(() => {
      this._sideCartFocusedCards.forEach(card => card.classList.remove('tb-side-cart-item--storm'));
      this._sideCartFocusedCards = [];
      this._sideCartStormTimer = undefined;
    }, 2600);
  },

  _clearSideCartFocusEffects() {
    if (this._sideCartFocusDelayTimer) {
      window.clearTimeout(this._sideCartFocusDelayTimer);
      this._sideCartFocusDelayTimer = undefined;
    }

    if (this._sideCartJiggleTimer) {
      window.clearTimeout(this._sideCartJiggleTimer);
      this._sideCartJiggleTimer = undefined;
    }

    if (this._sideCartStormTimer) {
      window.clearTimeout(this._sideCartStormTimer);
      this._sideCartStormTimer = undefined;
    }

    const focusedCards = Array.isArray(this._sideCartFocusedCards) ? this._sideCartFocusedCards : [];
    focusedCards.forEach(card => {
      card.classList.remove('tb-side-cart-item--storm', 'tb-side-cart-item--jiggle');
    });

    this._sideCartFocusedCards = [];
  },

  openSideCartDrawer() {
    this._resetMobileBarDragState();
    this.sideCartDrawerOpen = true;
    this._lockSideCartScroll();
  },

  closeSideCartDrawer() {
    this.sideCartDrawerOpen = false;
    this._unlockSideCartScroll();
  },

  _lockSideCartScroll() {
    if (typeof this.acquireBodyScrollLock === 'function') {
      this.acquireBodyScrollLock('side-cart');
    }
  },

  _unlockSideCartScroll() {
    if (typeof this.releaseBodyScrollLock === 'function') {
      this.releaseBodyScrollLock('side-cart');
    }
  },

  handleMobileBarTouchStart(event) {
    if (this.sideCartDrawerOpen) return;

    const touch = event?.touches?.[0];
    if (!touch) return;

    this._mobileBarDragState = {
      startY: touch.clientY,
      lastY: touch.clientY,
      dragging: true
    };

    const mobileBar = document.getElementById('tb-mobile-bar');
    if (!mobileBar) return;
    mobileBar.style.willChange = 'transform';
  },

  handleMobileBarTouchMove(event) {
    const state = this._mobileBarDragState;
    if (!state?.dragging || this.sideCartDrawerOpen) return;

    const touch = event?.touches?.[0];
    if (!touch) return;

    state.lastY = touch.clientY;
    const dragUpDistance = state.startY - touch.clientY;
    const openThreshold = 56;

    if (dragUpDistance >= openThreshold) {
      this._resetMobileBarDragState();
      this.openSideCartDrawer();
      return;
    }

    const clampedDistance = Math.max(0, Math.min(dragUpDistance, 96));

    if (clampedDistance > 10 && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const mobileBar = document.getElementById('tb-mobile-bar');
    if (!mobileBar) return;
    mobileBar.style.transform = clampedDistance > 0 ? `translateY(-${clampedDistance}px)` : '';
  },

  handleMobileBarTouchEnd(event) {
    const state = this._mobileBarDragState;
    if (!state) return;

    const changedTouch = event?.changedTouches?.[0];
    const endY = typeof changedTouch?.clientY === 'number' ? changedTouch.clientY : state.lastY;
    const dragUpDistance = state.startY - endY;
    const shouldOpenDrawer = dragUpDistance >= 56;

    this._resetMobileBarDragState();

    if (shouldOpenDrawer) {
      this.openSideCartDrawer();
    }
  },

  handleMobileBarTouchCancel() {
    this._resetMobileBarDragState();
  },

  _resetMobileBarDragState() {
    this._mobileBarDragState = undefined;

    const mobileBar = document.getElementById('tb-mobile-bar');
    if (!mobileBar) return;
    mobileBar.style.transform = '';
    mobileBar.style.willChange = '';
  },
};
